import React, { useState } from 'react';
import { Message } from '../types';
import { Send, Zap, GripHorizontal } from 'lucide-react';
import { ChatMessage } from './ChatMessage';

interface InternalContentProps {
  width: number;
  messages: Message[];
  systemPrompt: string;
  onSystemPromptChange: (prompt: string) => void;
  onSubmit: () => void;
  maxTokens: number;
  onIncreaseTokens: () => void;
}

// JSON Formatter Component
const JSONFormatter = ({ jsonData }) => {
  try {
    // Parse the JSON string if it's a string
    const jsonObj = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
    
    // Function to render JSON with syntax highlighting
    const renderJSON = (obj, indent = 0, isLast = true) => {
      const spaces = ' '.repeat(indent * 2);
      
      if (obj === null) {
        return <span className="json-null">null</span>;
      }
      
      if (typeof obj === 'undefined') {
        return <span className="json-undefined">undefined</span>;
      }
      
      if (typeof obj === 'boolean') {
        return <span className="json-boolean">{obj.toString()}</span>;
      }
      
      if (typeof obj === 'number') {
        return <span className="json-number">{obj}</span>;
      }
      
      if (typeof obj === 'string') {
        // Detect if string might be a date
        const datePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
        if (datePattern.test(obj)) {
          return <span className="json-date">"{obj}"</span>;
        }
        return <span className="json-string">"{obj}"</span>;
      }
      
      if (Array.isArray(obj)) {
        if (obj.length === 0) {
          return <span className="json-array">[]</span>;
        }
        
        return (
          <span className="json-array">
            [<br />
            {obj.map((item, index) => (
              <span key={index}>
                {spaces}{'  '}
                {renderJSON(item, indent + 1, index === obj.length - 1)}
                {index < obj.length - 1 && <span className="json-comma">,</span>}
                <br />
              </span>
            ))}
            {spaces}]
            {!isLast && ','}
          </span>
        );
      }
      
      // Object handling
      const keys = Object.keys(obj);
      if (keys.length === 0) {
        return <span className="json-object">{'{}'}</span>;
      }
      
      return (
        <span className="json-object">
          {'{'}<br />
          {keys.map((key, index) => (
            <span key={key}>
              {spaces}{'  '}
              <span className="json-key">"{key}"</span>: 
              {renderJSON(obj[key], indent + 1, index === keys.length - 1)}
              {index < keys.length - 1 && <span className="json-comma">,</span>}
              <br />
            </span>
          ))}
          {spaces}{'}'}
          {!isLast && ','}
        </span>
      );
    };
    
    return (
      <pre className="json-formatter">
        <code>{renderJSON(jsonObj)}</code>
      </pre>
    );
  } catch (error) {
    // If parsing fails, display the original content
    return (
      <pre className="json-error">
        <code>{jsonData}</code>
      </pre>
    );
  }
};

// Function to process message content for better readability
const processMessage = (message: Message): React.ReactNode => {
  if (!message || !message.content) {
    return null;
  }
  
  // Create a unique key for the message
  const messageKey = `msg-${message.id || Math.random().toString(36).substring(2, 9)}`;
  
  // Check if content is a string
  if (typeof message.content === 'string') {
    // Special case: Check for "The console output was:" pattern
    const consoleOutputRegex = /The console output was:[\s\n]*(\{[\s\S]*\}|\[[\s\S]*\])/s;
    const match = message.content.match(consoleOutputRegex);
    
    if (match && match[1]) {
      try {
        // Try to parse the JSON part
        const jsonContent = match[1];
        JSON.parse(jsonContent); // Validate it's valid JSON
        
        // If successful, render with JSON formatter and a prefix
        return (
          <div className="message-content p-3" key={messageKey}>
            <div className="rounded-md p-1 mb-2 font-medium text-sm text-gray-700 dark:text-gray-300">
              The console output was:
            </div>
            <JSONFormatter jsonData={jsonContent} />
          </div>
        );
      } catch (e) {
        // Not valid JSON in the match, continue with normal rendering
      }
    }
    
    // Regular JSON check (without prefix)
    try {
      // Check if the entire content looks like JSON
      if (
        (message.content.trim().startsWith('{') && message.content.trim().endsWith('}')) ||
        (message.content.trim().startsWith('[') && message.content.trim().endsWith(']'))
      ) {
        // Try to parse it
        JSON.parse(message.content);
        
        // If successful, render with JSON formatter
        return (
          <div className="message-content p-3" key={messageKey}>
            <div className="rounded-md p-1 mb-1 text-sm text-gray-500 dark:text-gray-400">JSON Message:</div>
            <JSONFormatter jsonData={message.content} />
          </div>
        );
      }
    } catch (e) {
      // Not valid JSON, continue with normal rendering
    }
  }
  
  // For non-JSON content, use the standard ChatMessage component
  return <ChatMessage key={messageKey} message={message} theme="light" />;
};

export function InternalContent({
  width,
  messages,
  systemPrompt,
  onSystemPromptChange,
  onSubmit,
  maxTokens,
  onIncreaseTokens
}: InternalContentProps) {
  const [messagesHeight, setMessagesHeight] = useState(300); // Default height for messages section

  const handleMessagesResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    const startY = e.clientY;
    const startHeight = messagesHeight;
    
    // Get reference to container element to calculate available height
    const containerElement = e.currentTarget.parentElement;
    const totalHeight = containerElement?.clientHeight || 600;
    
    // Calculate min/max values relative to container height
    const minHeight = Math.min(100, totalHeight * 0.1); // At least 10% or 100px
    const maxHeight = Math.max(totalHeight * 0.9, totalHeight - 150); // Leave at least 150px for system prompt
    
    console.log('Resize started - Total container height:', totalHeight);

    const handleMouseMove = (e: MouseEvent) => {
      const deltaY = e.clientY - startY;
      const newHeight = Math.max(minHeight, Math.min(maxHeight, startHeight + deltaY));
      console.log('Setting new message panel height:', newHeight);
      setMessagesHeight(newHeight);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <>
      <div
        className="border-l dark:border-gray-700 bg-white dark:bg-gray-800 flex flex-col text-sm overflow-hidden h-full"
        style={{ 
          width: `${width}px`, 
          minWidth: '250px',
          maxWidth: 'min(800px, calc(100vw - 20px))',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Main content area - flex container for both panels */}
        <div className="flex flex-col flex-grow overflow-hidden">
          {/* Internal Message Panel */}
          <div 
            className="overflow-y-auto border-b dark:border-gray-700"
            style={{ height: messagesHeight }}
          >
            <div className="sticky top-0 bg-white dark:bg-gray-800 p-3 border-b dark:border-gray-700 z-10">
              <div className="flex justify-between items-center">
                <h2 className="text-base font-semibold dark:text-white">Internal Messages</h2>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Max Tokens: {maxTokens}
                </span>
              </div>
            </div>
            <div className="internal-message">
              {messages.map((message, index) => (
                <div key={index} className="message-container p-1">
                  {processMessage(message)}
                </div>
              ))}
            </div>
          </div>

          {/* Resize handle */}
          <div
            className="h-1 hover:bg-blue-500 cursor-row-resize flex items-center justify-center select-none"
            onMouseDown={handleMessagesResizeStart}
          >
            <GripHorizontal className="w-4 h-4 text-gray-400" />
          </div>

          {/* System Prompt Section - should fill remaining space */}
          <div className="flex flex-col flex-grow overflow-hidden p-3 border-b dark:border-gray-700">
            <h2 className="text-base font-semibold mb-2 dark:text-white">System Prompt</h2>
            <div className="flex-grow overflow-hidden flex" style={{ position: "relative" }}>
              <textarea
                value={systemPrompt}
                onChange={(e) => onSystemPromptChange(e.target.value)}
                className="absolute inset-0 p-2 text-sm border rounded-lg dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                placeholder="Enter system prompt..."
                style={{ resize: "none", height: "100%", width: "100%" }}
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="p-3 flex flex-wrap gap-2">
          <button
            onClick={onSubmit}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            <Send className="w-3.5 h-3.5" />
            Submit
          </button>
          <button
            onClick={onIncreaseTokens}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-purple-500 text-white rounded-lg hover:bg-purple-600"
          >
            <Zap className="w-3.5 h-3.5" />
            Increase Tokens
          </button>
        </div>
      </div>
    </>
  );
}
